import React, { useEffect, useState } from 'react';
import mammoth from 'mammoth';
import LoadingIcon from "@/assets/common/ic_loading_blue.png";
import { UploadedFile } from '../../index';

interface DocxViewerProps {
  file: UploadedFile;
  onError: (error: string) => void;
}

const DocxViewer: React.FC<DocxViewerProps> = ({ file, onError }) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDocxContent = async () => {
      try {
        setIsLoading(true);
        onError('');

        // 将File对象转换为ArrayBuffer
        const arrayBuffer = await file.file.arrayBuffer();

        // 使用mammoth转换DOCX为HTML，配置选项移除图片和超链接
        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          {
            // 忽略所有图片，返回空的 src
            convertImage: mammoth.images.imgElement(async () => {
              return { src: "" };
            }),
            // 样式映射：将超链接转换为普通文本
            styleMap: [
              "a => span"  // 将所有超链接转换为 span 标签
            ]
          }
        );

        // 进一步清理：移除样式标签但保留文本内容
        let cleanHtml = result.value;
        // 移除所有图片标签
        cleanHtml = cleanHtml.replace(/<img[^>]*>/gi, '');
        // 将超链接标签替换为其文本内容
        cleanHtml = cleanHtml.replace(/<a[^>]*>(.*?)<\/a>/gi, '$1');

        // 移除表格标签（保留内容）
        cleanHtml = cleanHtml.replace(/<\/?table[^>]*>/gi, '');
        cleanHtml = cleanHtml.replace(/<\/?tbody[^>]*>/gi, '');
        cleanHtml = cleanHtml.replace(/<\/?thead[^>]*>/gi, '');
        cleanHtml = cleanHtml.replace(/<\/?tr[^>]*>/gi, ' '); // 表格行用空格分隔
        cleanHtml = cleanHtml.replace(/<\/?td[^>]*>/gi, ' '); // 单元格用空格分隔
        cleanHtml = cleanHtml.replace(/<\/?th[^>]*>/gi, ' '); // 表头单元格用空格分隔

        // 移除列表标签（保留内容）
        cleanHtml = cleanHtml.replace(/<\/?ul[^>]*>/gi, '');
        cleanHtml = cleanHtml.replace(/<\/?ol[^>]*>/gi, '');
        cleanHtml = cleanHtml.replace(/<\/?li[^>]*>/gi, '\n'); // 列表项用换行分隔

        // 清理多余的空格和换行
        cleanHtml = cleanHtml.replace(/\s+/g, ' ').trim();

        setHtmlContent(cleanHtml);

        // 输出转换警告（如果有）
        if (result.messages.length > 0) {
          console.warn('DOCX转换警告:', result.messages);
        }

      } catch (error) {
        console.error('DOCX加载失败:', error);
        onError('DOCX文件加载失败，请检查文件是否损坏');
      } finally {
        setIsLoading(false);
      }
    };

    loadDocxContent();
  }, [file, onError]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center mt-38">
        <img src={LoadingIcon} className="size-6 animate-spin" />
        <p className="text-#626773CC text-size-3.5 mt-4">正在解析DOCX文件...</p>
      </div>
    );
  }

  return (
    <div
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export default DocxViewer;
